// The two figures BidSheet computes on top of the price it is given.
//
// They are here rather than inside the component for the reason Sheet's gesture
// decisions are in sheetGesture.js: a pure function a test can call directly is
// pinned by the test, while the same arithmetic inside a `computed` is only
// reachable by driving the DOM into the state that reads it — and the state
// that reads the floor is three taps deep on a panel that has to be open.
//
// The floor is the one that HAS to be out here. It is derived in three places
// inside the tray — the `−` stepper's lower bound, the coloring as the dealer
// types, and the clamp at submit — and a fourth time on the server, which is
// where `proxySpent` comes from. Four derivations of one rule, and the failure
// when two of them drift is silent: the tray shows a live proxy the engine
// dropped, and the dealer finds out by not winning.

/**
 * The lowest ceiling the engine will still act on.
 *
 * The engine ignores a proxy at or under the current bid — it filters
 * `amount >= current + increment` — so a ceiling below this never fires and
 * says nothing while not firing.
 *
 * It reads the BASE increment, never the selected one. The engine's test is
 * whether a proxy can reach the next valid bid, so a dealer who has tapped the
 * `+$200` chip must not find the floor raised above what a $100 bid clears —
 * the chip chooses how hard to bid live, not what counts as a live proxy.
 *
 * `null` for an asset with no current bid, and that is a real answer rather
 * than a missing one: with no price there is nothing a ceiling can sit above,
 * and a bare one-increment floor would disable the `−` at $100, a number that
 * means nothing without a bid under it.
 */
export function proxyFloor({ price, baseIncrement }) {
  if (price === null || price === undefined) return null;
  // No increment, no floor — and for the same kind of reason. The engine's test
  // is whether a ceiling can reach the next valid bid, and an asset the engine
  // has not given an increment to has no next valid bid. A typed ceiling on one
  // is submitted as typed.
  if (baseIncrement === null || baseIncrement === undefined) return null;
  return price + baseIncrement;
}

/**
 * The amount the slide commits: the current bid plus the chosen multiple of the
 * asset's own increment.
 *
 * Every result is `price + k × increment` with `k ≥ 1`, which clears the current
 * bid AND satisfies the engine's rule that a raise be a whole number of
 * increments.
 *
 * One function rather than two because the button's LABEL and the `place-bid`
 * PAYLOAD are the same number, and they were computed in two places.
 */
export function bidAmount({ price, increment, multiplier }) {
  if (price === null || price === undefined) return null;
  // An asset with no increment has no live amount: every multiple of nothing is
  // nothing, and the arithmetic would be `NaN` on the control that commits the
  // money. The tray draws a dash and takes the control away.
  if (increment === null || increment === undefined) return null;
  return price + multiplier * increment;
}
